import { core, investments, plan as planNs, type Decimal } from '@map-my-money/engine';
import { comparePlans } from './compare.ts';
import { definitionsFor } from './definitions.ts';
import { money, round1 } from './money.ts';
import { decode, type WaypointInput, type WirePlan } from './run.ts';

export const OPPORTUNITY_YEARS = [1, 5, 10, 20] as const;
export const SAVE_PERCENTS = [100, 50, 25] as const;
const MAX_SAVE_MONTHS = 120;
export const PURCHASE_ID = 'what-if-purchase';

/** "Should I buy this?" for a price: share of monthly disposable income, months to save, opportunity cost. */
export function affordability(wire: WirePlan, price: Decimal, annualReturnPercent: Decimal | null) {
  const plan = decode(wire);
  const disposable = planNs.monthlyDisposable(plan);
  const disposableN = core.toDouble(disposable);
  const priceN = core.toDouble(price);
  const rate = annualReturnPercent ?? plan.configuration.expectedReturnPercent;
  return {
    price: money(price),
    monthly_disposable: money(disposable),
    percent_of_monthly_disposable: disposableN > 0 ? round1((priceN / disposableN) * 100) : null,
    months_to_save: SAVE_PERCENTS.map((percent) => {
      const monthly = (disposableN * percent) / 100;
      const months = monthly > 0 ? Math.ceil(priceN / monthly) : null;
      return {
        percent_of_disposable: percent,
        monthly_amount: money(core.fromDouble(Math.max(0, monthly))),
        months: months !== null && months <= MAX_SAVE_MONTHS ? months : null,
      };
    }),
    annual_return_percent: core.toDouble(rate),
    opportunity_cost: OPPORTUNITY_YEARS.map((years) => {
      const future = investments.lumpSumFutureValue(price, rate, years);
      return { years, invested_value: money(future), growth_forgone: money(core.sub(future, price)) };
    }),
  };
}

/** The purchase as a one-off expense on `date`, for its effect on the forecast. */
export const purchaseAsExpense = (wire: WirePlan, id: string, label: string, price: string, date: string): WirePlan => ({
  ...wire,
  transactions: [...(wire.transactions ?? []), { id, label, type: 'expense', amount: price, frequency: 'oneTime', startDate: date }],
});

/** check_affordability's whole result: the check plus the forecast if bought outright on `purchaseDate`. */
export function affordabilityView(wire: WirePlan, waypoints: readonly WaypointInput[], asOf: string, request: { price: string; item: string; purchaseDate?: string; annualReturnPercent?: string }) {
  const check = affordability(wire, core.dec(request.price), request.annualReturnPercent === undefined ? null : core.dec(request.annualReturnPercent));
  const bought = purchaseAsExpense(wire, PURCHASE_ID, request.item, request.price, request.purchaseDate ?? asOf);
  return {
    ...check,
    if_bought: comparePlans(wire, bought, waypoints, asOf),
    definitions: definitionsFor('projection'),
  };
}

export type AffordabilityResult = ReturnType<typeof affordabilityView>;
